import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { BannersnackCreative, Creative, DisplayCreative } from '@feathr/blackbox';
import { CreativeClass } from '@feathr/blackbox';
import { ArchiveModalV1, ContextMenu, toast } from '@feathr/components';
import CreativeModal from '@feathr/extender/components/Creatives/CreativeModal';
import { isMetaCreative } from '@feathr/extender/components/Creatives/Creatives.utils';
import DuplicateCreativeModal from '@feathr/extender/components/Creatives/DuplicateCreativeModal';
import { useStore } from '@feathr/extender/state';
import { getIconForAction, useToggle } from '@feathr/hooks';

import DownloadBannersnackFileMenuItem from '../../DownloadBannersnackFileMenuItem';

interface IProps {
  creative: Creative;
}

function ProjectCreativesOptions({ creative }: Readonly<IProps>): JSX.Element {
  const { Creatives } = useStore();
  const [isArchiveModalOpen, toggleArchiveModalOpen] = useToggle(false);
  const [isDuplicateModalOpen, toggleIsDuplicateModalOpen] = useToggle(false);
  const [isEditModalOpen, toggleEditModalOpen] = useToggle(false);
  const { t } = useTranslation();

  async function handleArchive(): Promise<void> {
    try {
      await creative.archive();
      toast(t(`Creative {{name}} has been archived.`, { name: creative.get('name') }));
      toggleArchiveModalOpen();
    } catch (e) {
      toast(t("Couldn't archive creative. Try again."), {
        type: 'error',
      });
    }
  }

  function handleDuplicateClose(): void {
    Creatives.refreshApiCache();
    toggleIsDuplicateModalOpen();
  }

  const type = creative.get('_cls');
  const channel = isMetaCreative(creative) ? 'meta' : 'display';
  const animated =
    type === CreativeClass.DisplayBannersnack
      ? (creative as BannersnackCreative).get('animated')
      : false;

  return (
    <>
      <ContextMenu buttonType={'icon'} position={'left-start'}>
        <ContextMenu.Item
          disabled={creative.isUpdating}
          onClick={toggleEditModalOpen}
          prefix={getIconForAction('edit')}
        >
          {t('Edit')}
        </ContextMenu.Item>
        <ContextMenu.Item
          disabled={creative.isUpdating}
          onClick={toggleIsDuplicateModalOpen}
          prefix={getIconForAction('duplicate')}
        >
          {t('Duplicate')}
        </ContextMenu.Item>
        <ContextMenu.Divider />
        {type === CreativeClass.DisplayBannersnack && (
          <>
            <DownloadBannersnackFileMenuItem
              creative={creative as BannersnackCreative}
              type={'jpg'}
            />
            <DownloadBannersnackFileMenuItem
              creative={creative as BannersnackCreative}
              type={'png'}
            />
            <DownloadBannersnackFileMenuItem
              creative={creative as BannersnackCreative}
              type={'pdf'}
            />
            {animated && (
              <>
                <DownloadBannersnackFileMenuItem
                  creative={creative as BannersnackCreative}
                  type={'animated-gif'}
                />
                <DownloadBannersnackFileMenuItem
                  creative={creative as BannersnackCreative}
                  type={'mp4'}
                />
              </>
            )}
            <ContextMenu.Divider />
          </>
        )}
        <ContextMenu.Item
          onClick={toggleArchiveModalOpen}
          prefix={getIconForAction('archive')}
          theme={'danger'}
        >
          {t('Archive')}
        </ContextMenu.Item>
      </ContextMenu>
      {isDuplicateModalOpen && (
        <DuplicateCreativeModal
          channel={channel}
          defaultSelectedCreatives={[creative as DisplayCreative]}
          eventId={creative.get('event')}
          isOpen={isDuplicateModalOpen}
          onBack={handleDuplicateClose}
          onClose={handleDuplicateClose}
        />
      )}
      {isArchiveModalOpen && (
        <ArchiveModalV1
          model={creative}
          onClose={toggleArchiveModalOpen}
          onConfirm={handleArchive}
          t={t}
        />
      )}
      {isEditModalOpen && (
        <CreativeModal
          creative={creative as DisplayCreative}
          onClose={toggleEditModalOpen}
          opened={true}
        />
      )}
    </>
  );
}

export default observer(ProjectCreativesOptions);
